// Registration form definitions for all 6 categories.
// Each form is multi-step. Field types: text, textarea, number, email, phone, select, multiselect, radio, checkbox

export const FORM_CONFIGS = {
  performer: {
    title: { en: "Performer Registration", id: "Pendaftaran Penampil", ja: "出演者登録" },
    intro: {
      en: "Register your group to perform at the IJFF 2026 stage. A refundable deposit is required to confirm your slot.",
      id: "Daftarkan grup Anda untuk tampil di panggung IJFF 2026. Deposit yang dapat dikembalikan diperlukan.",
      ja: "IJFF 2026のステージ出演申込。デポジット（返金可）が必要です。",
    },
    steps: [
      {
        title: "Group Profile",
        fields: [
          { name: "group_name", label: "Group / Artist Name", type: "text", required: true },
          { name: "leader_name", label: "Leader / Contact Person", type: "text", required: true },
          { name: "phone", label: "Phone Number (with country code)", type: "phone", required: true },
          { name: "members_count", label: "Number of Members", type: "number", required: true },
          { name: "country", label: "Country of Origin", type: "select", required: true,
            options: ["Indonesia", "Japan", "Other"] },
          { name: "province_city", label: "Province / City of Origin", type: "text", required: true },
        ],
      },
      {
        title: "Performance Details",
        fields: [
          { name: "performance_type", label: "Performance Type", type: "select", required: true,
            options: ["Traditional Dance", "Modern Dance", "Traditional Music", "Band / Modern Music", "Cosplay Performance", "Cultural Ceremony", "Other"] },
          { name: "song_dance_origin", label: "Where do your original songs / dances come from?", type: "textarea", required: true,
            help: "e.g., 'Saman dance from Aceh', 'Enka from Osaka'" },
          { name: "unique_point", label: "What makes you different from other performers of the same city / province?", type: "textarea", required: true },
          { name: "duration_minutes", label: "Requested Performance Duration (minutes)", type: "number", required: true },
          { name: "stage_requirements", label: "Stage / Sound / Lighting Requirements", type: "textarea", required: false },
          { name: "video_link", label: "Video Sample Link (YouTube / Instagram)", type: "text", required: false },
        ],
      },
      {
        title: "Deposit & Confirmation",
        fields: [
          { name: "deposit_agreement", label: "I agree to pay the registration deposit to guarantee attendance. Deposit is refundable within 14 days after the event upon confirmed performance.", type: "checkbox", required: true },
        ],
      },
    ],
  },

  restaurant: {
    title: { en: "Restaurant / Catering Booth Registration", id: "Pendaftaran Booth Restoran / Katering", ja: "飲食・ケータリング出店申込" },
    intro: {
      en: "Register your food booth. All food participants must comply with Japanese food safety regulations. The organizer will share the rulebook after registration.",
      id: "Daftarkan booth makanan Anda. Semua peserta wajib mematuhi peraturan keamanan pangan Jepang.",
      ja: "飲食ブース申込。日本の食品衛生法に準拠していただきます。",
    },
    steps: [
      {
        title: "Business Info",
        fields: [
          { name: "business_name", label: "Business / Restaurant Name", type: "text", required: true },
          { name: "owner_name", label: "Owner Name", type: "text", required: true },
          { name: "phone", label: "Phone Number", type: "phone", required: true },
          { name: "address", label: "Business Address", type: "textarea", required: true },
          { name: "country", label: "Country of Origin", type: "select", required: true,
            options: ["Indonesia", "Japan", "Other"] },
          { name: "cuisine_type", label: "Cuisine Type", type: "text", required: true, help: "e.g., Padang, Ramen, BBQ" },
        ],
      },
      {
        title: "Booth & Menu",
        fields: [
          { name: "booth_type", label: "Booth Type", type: "select", required: true,
            options: ["Food Booth (330,000 JPY)", "Catering Booth (242,000 JPY)"] },
          { name: "booth_count", label: "Number of Booths", type: "select", required: true, options: ["1", "2"] },
          { name: "menu_items", label: "Menu Items (list with prices)", type: "textarea", required: true },
          { name: "needs_gas", label: "Requires gas burner", type: "checkbox", required: false },
          { name: "needs_refrigerator", label: "Requires refrigerator", type: "checkbox", required: false },
          { name: "needs_sink", label: "Requires sink with clean water", type: "checkbox", required: false },
          { name: "food_safety_cert", label: "Do you have a Japanese food safety license (食品衛生責任者)?", type: "select", required: true, options: ["Yes", "No, will apply", "Working with a licensed partner"] },
        ],
      },
      {
        title: "Logistics",
        fields: [
          { name: "vehicle_info", label: "Vehicle number & model for loading/unloading (per day)", type: "textarea", required: false },
          { name: "pr_photo_link", label: "PR Photo / Google Drive Link", type: "text", required: false },
          { name: "pr_text", label: "PR Text (within 120 chars)", type: "textarea", required: false, maxLength: 120 },
          { name: "hygiene_agreement", label: "I confirm we will comply with Japanese food hygiene rules and organizer's guidelines.", type: "checkbox", required: true },
        ],
      },
    ],
  },

  trader: {
    title: { en: "Trader / Services / Local Government Registration", id: "Pendaftaran Pedagang / Jasa / Pemda", ja: "物販・サービス・自治体申込" },
    intro: {
      en: "Register for product sales, services or local government promotion booths.",
      id: "Daftarkan booth pedagang produk, jasa, atau promosi pemerintah daerah.",
      ja: "物販、サービス、自治体プロモーションブースの申込。",
    },
    steps: [
      {
        title: "Organization Info",
        fields: [
          { name: "subtype", label: "Participant Type", type: "select", required: true,
            options: ["Product Trader", "Service Provider", "Craft / Fashion / Merchandise", "Local Government (Indonesia)", "Local Government (Japan)"] },
          { name: "org_name", label: "Company / Organization Name", type: "text", required: true },
          { name: "contact_name", label: "Contact Person", type: "text", required: true },
          { name: "phone", label: "Phone Number", type: "phone", required: true },
          { name: "address", label: "Address", type: "textarea", required: true },
          { name: "website", label: "Website / Social Media", type: "text", required: false },
        ],
      },
      {
        title: "Booth Selection",
        fields: [
          { name: "booth_type", label: "Booth Type", type: "select", required: true,
            options: ["Exhibition Booth (264,000 JPY)", "Bring-In Booth (198,000 JPY)"] },
          { name: "booth_count", label: "Number of Booths", type: "select", required: true, options: ["1", "2", "3"] },
          { name: "products_services", label: "Products / Services to be sold or displayed", type: "textarea", required: true },
          { name: "special_equipment", label: "Special Equipment Requests (beyond 2 tables + 4 chairs)", type: "textarea", required: false },
        ],
      },
    ],
  },

  business_matching: {
    title: { en: "Business Matching Registration", id: "Pendaftaran Business Matching", ja: "ビジネスマッチング申込" },
    intro: {
      en: "Register as a Japanese employer, receiving/supporting organization, or Indonesian sending organization to participate in the Business Matching tent.",
      id: "Daftarkan perusahaan Anda untuk kegiatan Business Matching di tenda besar.",
      ja: "日本の雇用企業・受入機関、またはインドネシアの送出機関としてビジネスマッチングにご登録ください。",
    },
    steps: [
      {
        title: "Organization",
        fields: [
          { name: "role", label: "Your Role", type: "select", required: true,
            options: ["Japan Employer Company", "Japan Receiving / Supporting Organization", "Indonesia Sending Organization"] },
          { name: "org_name", label: "Company / Organization Name", type: "text", required: true },
          { name: "industry", label: "Industry", type: "select", required: true,
            options: ["Manufacturing", "Construction", "Agriculture / Fishery", "Nursing / Care Work", "Food & Beverage", "Hospitality", "IT / Engineering", "Automotive", "Retail", "Other"] },
          { name: "contact_name", label: "Representative Name", type: "text", required: true },
          { name: "phone", label: "Phone Number", type: "phone", required: true },
          { name: "website", label: "Website", type: "text", required: false },
        ],
      },
      {
        title: "Business Matching Data",
        fields: [
          { name: "workers_number", label: "Number of workers you want to employ / can supply", type: "number", required: true },
          { name: "required_skills", label: "Required / Available Skills", type: "textarea", required: true,
            help: "e.g., N4 Japanese, welding certificate, 3 years of nursing experience" },
          { name: "visa_type", label: "Target Visa / Program", type: "select", required: false,
            options: ["Specified Skilled Worker (SSW)", "Technical Intern Training (TITP)", "Engineer / Specialist in Humanities", "Nursing Care", "Other"] },
          { name: "additional_notes", label: "Additional Requirements / Offer", type: "textarea", required: false },
        ],
      },
    ],
  },

  sponsor: {
    title: { en: "Sponsor Registration", id: "Pendaftaran Sponsor", ja: "スポンサー登録" },
    intro: {
      en: "Become a Partner (max 15M JPY), Platinum, Gold, Silver or Bronze sponsor. The organizer team will contact you with the full sponsorship deck within 3 business days.",
      id: "Menjadi Partner (maks 15 juta JPY), Platinum, Gold, Silver, atau Bronze sponsor. Tim panitia akan menghubungi Anda dalam 3 hari kerja.",
      ja: "パートナー（最大1,500万円）、プラチナ、ゴールド、シルバー、ブロンズスポンサー登録。3営業日以内にご連絡します。",
    },
    steps: [
      {
        title: "Company",
        fields: [
          { name: "company_name", label: "Company Name", type: "text", required: true },
          { name: "contact_name", label: "Contact Person", type: "text", required: true },
          { name: "position", label: "Position / Title", type: "text", required: true },
          { name: "phone", label: "Phone Number", type: "phone", required: true },
          { name: "website", label: "Company Website", type: "text", required: false },
          { name: "industry", label: "Industry", type: "text", required: true },
        ],
      },
      {
        title: "Sponsorship Preference",
        fields: [
          { name: "tier", label: "Preferred Sponsorship Tier", type: "select", required: true,
            options: ["Co-Partner (8,000,000 JPY)", "Platinum (4,000,000 JPY)", "Gold (3,000,000 JPY)", "Silver (1,000,000 JPY)", "Custom / Discuss"] },
          { name: "goals", label: "Your Sponsorship Goals", type: "textarea", required: true,
            help: "Brand visibility, product launch, HR recruitment, community engagement, etc." },
          { name: "custom_benefits", label: "Custom Benefits Requested", type: "textarea", required: false },
        ],
      },
    ],
  },
};
